import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { Button } from '@pets-ui/Button/Button'
import { ButtonToggleVisiblePassword } from '@pets-ui/ButtonToggleVisiblePassword/ButtonToggleVisiblePassword'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Input } from '@pets-ui/Input/Input'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type AxiosError } from 'axios'
import queryString from 'query-string'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequestLoginByPassword } from '../../../hooks/apis/wp/useRequestLoginByPassword'
import { useLineInvitationCheck } from '../../../hooks/useLineInvitationCheck'
import { type LoginByPasswordErrorResponse } from '../../../types/apiErrorResponse'
import { getAccount, resetCosignCookies } from '../../../utils/cookie'
import { resolveLineInvitationRedirectUrl } from '../../../utils/lineInvitationUrl'
import { getRegisterOrLoginRedirectUrl } from '../../../utils/wordPressRoute'
import { useContextCosign } from '../../useContextCosign'
import { Service } from '../Service'
import { CardTitleWithIcon } from './CardTitleWithIcon'
import { DividerOr } from './DividerOr'
import { LoginStep } from './types'
import { useContextLogin } from './useContextLogin'

const styles = stylex.create({
  forgotPasswordLink: {
    color: colors.blue600,
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  backToLoginButton: {
    margin: '0 auto',
    display: 'block',
  },
})

export function PasswordLogin() {
  const { t } = useTranslation()
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { environment, campaignCode, redirectUrl, tokenLoginUrl, lineInvitation } = useContextCosign()
  const { backStep, setStep } = useContextLogin()
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string>()
  const { trigger, isMutating } = useRequestLoginByPassword()
  // TODO: 密碼功能 Phase2
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [remainingTimes, setRemainingTimes] = useState<number>()
  const account = getAccount()

  const lineInvitationCheck = useLineInvitationCheck({
    account,
    enabled: lineInvitation,
  })

  const handleClickForgotPassword = () => {
    sendEvent({ eventName: 'Forget password', eventParams: { event_category: 'Password' } })
    setStep(LoginStep.FORGET_PASSWORD)
  }

  const handleClickPasswordLogin = async () => {
    if (!account) return

    const originalRedirectUrl = redirectUrl || getRegisterOrLoginRedirectUrl()
    const resolvedRedirectUrl = lineInvitation
      ? resolveLineInvitationRedirectUrl(originalRedirectUrl, lineInvitationCheck)
      : originalRedirectUrl

    try {
      const { data } = await trigger({
        account,
        password,
        redirectUrl: resolvedRedirectUrl,
        tokenLoginUrl,
      })
      setError('')
      resetCosignCookies()
      if (environment === 'wordPress') {
        window.location.href = data.data.url
      }
      if (environment === 'merchant' && campaignCode) {
        const queryParams = queryString.parse(data.data.url.split('?')[1] ?? '')
        window.location.href = `${tokenLoginUrl}?token=${queryParams.token}`
      }
      if (environment === 'campaign' && campaignCode) {
        window.location.href = data.data.url
      }
    }
    catch (e) {
      const error = e as AxiosError<LoginByPasswordErrorResponse>
      if (error.response?.status === 422) {
        setRemainingTimes(error.response.data.detail.remainingTimes)
        setError(String(t('error.incorrectPassword')))
        return
      }
      console.error(error)
    }
  }

  const handleClickBackLogin = () => {
    resetCosignCookies()
    setStep(LoginStep.LOGIN)
  }

  return (
    <>
      <CardTitleWithIcon
        title={t('login.passwordLogin')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={backStep}
      />
      <VStack gap={4}>
        <VStack gap={1}>
          <Input
            value={password}
            onChange={e => setPassword(e.target.value)}
            label={t('login.enterPassword')}
            placeholder={String(t('login.pleaseEnter'))}
            fullWidth
            type={showPassword ? 'text' : 'password'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleClickPasswordLogin()
              }
            }}
            endAdornment={<ButtonToggleVisiblePassword show={showPassword} onClick={() => setShowPassword(!showPassword)} />}
            error={!!error}
            helpText={error}
          />
          {environment === 'wordPress' && <Text variant="caption" rootStyle={styles.forgotPasswordLink} onClick={handleClickForgotPassword}>{t('login.forgetPassword')}</Text>}
        </VStack>
        <Button
          loading={isMutating}
          onClick={handleClickPasswordLogin}
          disabled={password.trim() === ''}
        >
          {t('button.confirm')}
        </Button>
      </VStack>
      <DividerOr />
      <Button
        variant="text"
        color="grey"
        rootStyle={styles.backToLoginButton}
        onClick={handleClickBackLogin}
      >
        {t('login.returnToLogin')}
      </Button>
      <Service />
    </>
  )
}
